/**
 * AuditLogs 頁面
 * 稽核日誌管理
 */

import { useState, useMemo } from 'react';
import { Layout } from '../../components/Layout';
import { get } from '../../api/superAdminClient';
import useSWR from 'swr';
import { Pagination } from '../../components/Pagination';
import { FilterBar, FilterItem } from '../../components/FilterBar';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { Modal } from '../../components/Modal';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';

/**
 * 稽核日誌列表回應類型
 */
interface AuditLogListResponse {
  success: boolean;
  data: {
    logs: Array<{
      id: string;
      action: string;
      actorType: string;
      actorId: string;
      actorEmail: string | null;
      tenantId: string | null;
      resourceType: string | null;
      resourceId: string | null;
      details: Record<string, unknown> | null;
      ipAddress: string | null;
      userAgent: string | null;
      createdAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * 稽核日誌詳情回應類型
 */
interface AuditLogDetailResponse {
  success: boolean;
  data: {
    id: string;
    action: string;
    actorType: string;
    actorId: string;
    actorEmail: string | null;
    tenantId: string | null;
    resourceType: string | null;
    resourceId: string | null;
    details: Record<string, unknown> | null;
    ipAddress: string | null;
    userAgent: string | null;
    createdAt: string;
  };
}

/**
 * AuditLogs 頁面
 */
export function AuditLogs() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [actorTypeFilter, setActorTypeFilter] = useState<
    'super_admin' | 'tenant_user' | ''
  >('');
  const [tenantIdFilter, setTenantIdFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // 取得租戶列表（用於篩選）
  const { data: tenantsData } = useSWR<{
    success: boolean;
    data: {
      tenants: Array<{ id: string; name: string }>;
    };
  }>('/api/super-admin/tenants?page=1&limit=100', get as any);

  // 構建查詢參數
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', '50');
    if (search) {
      params.set('search', search);
    }
    if (actionFilter) {
      params.set('action', actionFilter);
    }
    if (actorTypeFilter) {
      params.set('actorType', actorTypeFilter as string);
    }
    if (tenantIdFilter) {
      params.set('tenantId', tenantIdFilter);
    }
    if (startDateFilter) {
      params.set('startDate', new Date(startDateFilter).toISOString());
    }
    if (endDateFilter) {
      params.set('endDate', new Date(endDateFilter).toISOString());
    }
    return params.toString();
  }, [
    page,
    search,
    actionFilter,
    actorTypeFilter,
    tenantIdFilter,
    startDateFilter,
    endDateFilter,
  ]);

  // 取得稽核日誌列表
  const { data, error, isLoading, mutate } = useSWR<AuditLogListResponse>(
    `/api/super-admin/audit-logs?${queryParams}`,
    get as any
  );

  const handleViewDetail = (logId: string) => {
    setSelectedLogId(logId);
    setIsDetailModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearch('');
    setActionFilter('');
    setActorTypeFilter('');
    setTenantIdFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setPage(1);
  };

  const hasActiveFilters = !!(
    search ||
    actionFilter ||
    actorTypeFilter ||
    tenantIdFilter ||
    startDateFilter ||
    endDateFilter
  );

  return (
    <Layout title="稽核日誌">
      <div className="space-y-4">
        {/* 搜尋和篩選 */}
        <div className="space-y-4">
          {/* 搜尋欄 */}
          <div className="bg-white rounded-lg shadow p-4">
            <input
              type="text"
              placeholder="搜尋操作、執行者、資源..."
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* 篩選器 */}
          <FilterBar
            onClear={handleClearFilters}
            showClearButton={hasActiveFilters}
          >
            <FilterItem label="操作類型">
              <input
                type="text"
                value={actionFilter}
                onChange={e => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="例如: tenant.created"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </FilterItem>
            <FilterItem label="執行者類型">
              <select
                value={actorTypeFilter}
                onChange={e => {
                  setActorTypeFilter(
                    e.target.value as 'super_admin' | 'tenant_user' | ''
                  );
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                <option value="super_admin">Super Admin</option>
                <option value="tenant_user">租戶使用者</option>
              </select>
            </FilterItem>
            <FilterItem label="租戶">
              <select
                value={tenantIdFilter}
                onChange={e => {
                  setTenantIdFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                {tenantsData?.data?.tenants?.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </FilterItem>
            <FilterItem label="開始日期">
              <input
                type="date"
                value={startDateFilter}
                onChange={e => {
                  setStartDateFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </FilterItem>
            <FilterItem label="結束日期">
              <input
                type="date"
                value={endDateFilter}
                onChange={e => {
                  setEndDateFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </FilterItem>
          </FilterBar>
        </div>

        {/* 稽核日誌列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ErrorDisplay error={error} onRetry={() => mutate()} />
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      時間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      執行者
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      資源
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.data?.logs?.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(
                          new Date(log.createdAt),
                          'yyyy-MM-dd HH:mm:ss',
                          { locale: zhTW }
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <span className="font-medium">{log.action}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>
                          <div className="font-medium">
                            {log.actorEmail || log.actorId}
                          </div>
                          <div className="text-xs text-gray-400">
                            {log.actorType === 'super_admin'
                              ? 'Super Admin'
                              : '租戶使用者'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {log.resourceType ? (
                          <div>
                            <div>{log.resourceType}</div>
                            {log.resourceId && (
                              <div className="text-xs text-gray-400 truncate max-w-xs">
                                {log.resourceId}
                              </div>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewDetail(log.id)}
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          詳情
                        </button>
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 text-center text-gray-500"
                      >
                        尚無稽核日誌
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* 分頁 */}
              {data?.data && data.data.totalPages > 1 && (
                <div className="px-6 py-4 border-t">
                  <Pagination
                    currentPage={data.data.page}
                    totalPages={data.data.totalPages}
                    total={data.data.total}
                    limit={data.data.limit}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* 日誌詳情 Modal */}
        <AuditLogDetailModal
          logId={selectedLogId}
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedLogId(null);
          }}
        />
      </div>
    </Layout>
  );
}

/**
 * 稽核日誌詳情 Modal
 */
interface AuditLogDetailModalProps {
  logId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function AuditLogDetailModal({
  logId,
  isOpen,
  onClose,
}: AuditLogDetailModalProps) {
  const { data, error, isLoading } = useSWR<AuditLogDetailResponse>(
    logId ? `/api/super-admin/audit-logs/${logId}` : null,
    get as any
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="稽核日誌詳情" size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <ErrorDisplay error={error} />
      ) : data?.data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                操作
              </label>
              <p className="text-gray-900">{data.data.action}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                執行者類型
              </label>
              <p className="text-gray-900">
                {data.data.actorType === 'super_admin'
                  ? 'Super Admin'
                  : '租戶使用者'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                執行者 ID
              </label>
              <p className="text-gray-900 font-mono text-sm">
                {data.data.actorId}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                執行者 Email
              </label>
              <p className="text-gray-900">{data.data.actorEmail || '-'}</p>
            </div>
            {data.data.tenantId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  租戶 ID
                </label>
                <p className="text-gray-900 font-mono text-sm">
                  {data.data.tenantId}
                </p>
              </div>
            )}
            {data.data.resourceType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  資源類型
                </label>
                <p className="text-gray-900">{data.data.resourceType}</p>
              </div>
            )}
            {data.data.resourceId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  資源 ID
                </label>
                <p className="text-gray-900 font-mono text-sm">
                  {data.data.resourceId}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IP 位址
              </label>
              <p className="text-gray-900">{data.data.ipAddress || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                建立時間
              </label>
              <p className="text-gray-900">
                {format(new Date(data.data.createdAt), 'yyyy-MM-dd HH:mm:ss', {
                  locale: zhTW,
                })}
              </p>
            </div>
          </div>

          {data.data.details && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                詳細資訊
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-sm text-gray-900 overflow-x-auto">
                  {JSON.stringify(data.data.details, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {data.data.userAgent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Agent
              </label>
              <p className="text-gray-900 text-sm break-all">
                {data.data.userAgent}
              </p>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
